// -----------------------------------------------------------------------------
// QP Template structure model
//
// A template has:
//   questions: Question[]  (optional — top-level "direct" questions, no section)
//   sections: Section[]    (optional — sectioned questions)
//
// Section:
//   id, label (A / B / I / 1), name ("Section A" / "Part A" / "Group A"),
//   instructions, attemptRule { type: 'all' | 'any', count, total },
//   questions: Question[]
//
// Question (top-level or sub-question):
//   id, number ("1", "11.(a)", "a", "i"),
//   marks,
//   co, unit, bl, type, difficulty, po, knowledgePriority   (all optional)
//   subQuestions: Question[]                (one or more levels deep)
//   connectorAfter: { type: 'OR' | 'AND', label } | null   (links to NEXT sibling)
// -----------------------------------------------------------------------------

export const OPTIONAL_COLUMNS = [
  { key: 'co', label: 'CO', fullLabel: 'Course Outcome' },
  { key: 'unit', label: 'Unit', fullLabel: 'Unit / Module' },
  { key: 'bl', label: 'BL', fullLabel: 'Learning Taxonomy (Bloom\'s Level)' },
  { key: 'type', label: 'Type', fullLabel: 'Question Type' },
  { key: 'difficulty', label: 'Diff.', fullLabel: 'Difficulty Level' },
  { key: 'knowledgePriority', label: 'KP', fullLabel: 'Knowledge Priority' },
  { key: 'po', label: 'PO', fullLabel: 'Program Outcome' },
];

export const DEFAULT_VISIBLE_COLUMNS = ['co', 'unit', 'bl', 'type', 'difficulty'];

// Map each optional column key to the QP Master Values segment code so dropdown
// options can be populated from the user-managed master list.
export const COLUMN_TO_SEGMENT = {
  co: 'course_outcomes',
  unit: 'unit',
  bl: 'learning_taxonomy',
  type: 'question_type',
  difficulty: 'difficulty_level',
  knowledgePriority: 'knowledge_priority',
  po: 'program_outcomes',
};

// ---------- ID generation (client-side only, stable within a session) ----------

let idCounter = 0;
export const newId = (prefix = 'id') => {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${idCounter}`;
};

// ---------- Factory helpers ----------

export const emptyQuestion = () => ({
  id: newId('q'),
  number: '',
  marks: 0,
  co: '',
  unit: '',
  bl: '',
  type: '',
  difficulty: '',
  knowledgePriority: '',
  po: '',
  subQuestions: [],
  connectorAfter: null,
});

export const emptySection = (label = 'A') => ({
  id: newId('sec'),
  label,
  name: `Section ${label}`,
  instructions: '',
  attemptRule: { type: 'all', count: 0, total: 0 },
  questions: [],
});

export const emptyStructure = () => ({
  questions: [],
  sections: [],
});

export const connectorLabel = (type) =>
  type === 'OR' ? 'Student attempts either question' : 'Both questions are compulsory';

// ---------- Recursive utilities ----------

export const walkQuestions = (questions, fn, parent = null) => {
  for (const q of questions) {
    fn(q, parent);
    if (q.subQuestions?.length) {
      walkQuestions(q.subQuestions, fn, q);
    }
  }
};

/**
 * Sum marks of a question including all nested sub-questions (flat view).
 * Exported so UI components can surface per-question rollups (e.g. to warn on
 * OR-paired siblings whose totals disagree).
 */
export const sumQuestionFlat = (q) => {
  let total = Number(q.marks) || 0;
  if (q.subQuestions?.length) {
    for (const sq of q.subQuestions) total += sumQuestionFlat(sq);
  }
  return round2(total);
};

/**
 * True when the OR connector after `siblings[index]` links two questions
 * whose flat totals are different. Used for inline warnings next to the
 * OR connector row.
 */
export const hasOrMismatchAfter = (siblings, index) => {
  const q = siblings?.[index];
  const next = siblings?.[index + 1];
  if (!q || !next) return false;
  if (q.connectorAfter?.type !== 'OR') return false;
  return sumQuestionFlat(q) !== sumQuestionFlat(next);
};

/**
 * Attempt-adjusted sum for a sibling list.
 * Consecutive siblings linked by OR collapse into their max.
 */
const sumAttempt = (questions) => {
  let sum = 0;
  let i = 0;
  while (i < questions.length) {
    let groupMax = sumQuestionFlat(questions[i]);
    while (questions[i]?.connectorAfter?.type === 'OR' && questions[i + 1]) {
      groupMax = Math.max(groupMax, sumQuestionFlat(questions[i + 1]));
      i += 1;
    }
    sum += groupMax;
    i += 1;
  }
  return sum;
};

/**
 * Compute totals across the entire structure.
 * Returns { totalSections, totalQuestions, setMarks, attemptMarks }.
 */
export const computeTotals = (structure) => {
  const sections = structure?.sections || [];
  const flatQuestions = Array.isArray(structure?.questions) ? structure.questions : [];

  let totalQuestions = 0;
  let setMarks = 0;

  const countAndSum = (questions) => {
    for (const q of questions) {
      totalQuestions += 1;
      setMarks += Number(q.marks) || 0;
      if (q.subQuestions?.length) countAndSum(q.subQuestions);
    }
  };

  countAndSum(flatQuestions);
  for (const sec of sections) countAndSum(sec.questions || []);

  let attemptMarks = sumAttempt(flatQuestions);
  for (const sec of sections) attemptMarks += sumAttempt(sec.questions || []);

  return {
    totalSections: sections.length,
    totalQuestions,
    setMarks: round2(setMarks),
    attemptMarks: round2(attemptMarks),
  };
};

/**
 * Per-section totals — used for section header chips (e.g. "25/15 marks").
 */
export const computeSectionTotals = (section) => {
  let set = 0;
  let count = 0;
  const walk = (arr) => {
    for (const q of arr) {
      set += Number(q.marks) || 0;
      count += 1;
      if (q.subQuestions?.length) walk(q.subQuestions);
    }
  };
  walk(section.questions || []);

  const attempt = sumAttempt(section.questions || []);

  return { setMarks: round2(set), attemptMarks: round2(attempt), questionCount: count };
};

// ---------- Marks validation ----------

/**
 * Sum of direct (immediate) sub-question marks — used to validate against parent.
 */
export const sumDirectSubMarks = (question) => {
  if (!question.subQuestions?.length) return 0;
  return round2(
    question.subQuestions.reduce((a, sq) => a + (Number(sq.marks) || 0), 0)
  );
};

/**
 * True if the question has sub-questions AND its marks disagree with their sum.
 * A parent marks of 0 is treated as "not set" and is never flagged.
 */
export const hasMarksMismatch = (question) => {
  if (!question.subQuestions?.length) return false;
  const parent = Number(question.marks) || 0;
  if (parent === 0) return false;
  return round2(parent) !== sumDirectSubMarks(question);
};

/**
 * Walk the whole structure and return a list of human-readable error strings.
 * Two classes of error are reported:
 *   1. Parent/sub marks mismatch — a parent with explicit marks disagrees with
 *      the sum of its direct sub-questions.
 *   2. OR-pair marks mismatch — two siblings linked by an OR connector carry
 *      different flat totals (the "attempt either" contract requires equal
 *      weight so the student's score is independent of the choice).
 */
export const validateStructureMarks = (structure) => {
  const errors = [];
  const walk = (siblings, pathPrefix) => {
    for (let i = 0; i < siblings.length; i += 1) {
      const q = siblings[i];
      const qPath = pathPrefix ? `${pathPrefix} → ${q.number || '?'}` : (q.number || '?');

      // 1. Parent vs. sum-of-sub-questions
      if (hasMarksMismatch(q)) {
        errors.push(
          `${qPath}: parent is ${q.marks} but sub-questions total ${sumDirectSubMarks(q)}`
        );
      }

      // 2. OR-paired sibling totals
      const next = siblings[i + 1];
      if (q.connectorAfter?.type === 'OR' && next) {
        const a = sumQuestionFlat(q);
        const b = sumQuestionFlat(next);
        if (a !== b) {
          const nPath = pathPrefix
            ? `${pathPrefix} → ${next.number || '?'}`
            : (next.number || '?');
          errors.push(`${qPath} OR ${nPath}: OR options must have equal marks (${a} ≠ ${b})`);
        }
      }

      if (q.subQuestions?.length) walk(q.subQuestions, qPath);
    }
  };
  for (const sec of structure?.sections || []) {
    walk(sec.questions || [], sec.label || sec.name || 'Section');
  }
  walk(structure?.questions || [], 'Q');
  return errors;
};

// ---------- Tree mutation helpers ----------

/**
 * Build an OR-sibling for an existing question.
 * Uses the source number to derive a sensible next letter/label.
 */
export const buildOrSibling = (sourceQuestion, siblingIndex) => {
  const sib = emptyQuestion();

  // Derive a reasonable number for the new sibling.
  // e.g., "11.(a)" -> "11.(b)";  "1"  -> "2" (or a letter style is clearer as pair)
  const src = String(sourceQuestion.number || '').trim();
  const letterParen = src.match(/^(.*?)\(([a-zA-Z])\)$/);
  const dotLetter = src.match(/^(.*?)\.?([a-zA-Z])$/);

  if (letterParen) {
    const nextLetter = String.fromCharCode(letterParen[2].toLowerCase().charCodeAt(0) + 1);
    sib.number = `${letterParen[1]}(${nextLetter})`;
  } else if (dotLetter && dotLetter[1]) {
    const nextLetter = String.fromCharCode(dotLetter[2].toLowerCase().charCodeAt(0) + 1);
    sib.number = `${dotLetter[1]}${nextLetter}`;
  } else if (/^\d+$/.test(src)) {
    // Pair a plain number like "1" with "1.b" (so the pair reads as OR options of Q1)
    sib.number = `${src}.b`;
    // Also backfill: if source number was just a digit, no label we can change (handled by caller)
  } else {
    sib.number = `${src || (siblingIndex + 1)}.b`;
  }

  return sib;
};

const round2 = (n) => Math.round(n * 100) / 100;
